import {
  AlertTriangle,
  Check,
  Info,
  Loader2,
  MapPin,
  Navigation,
  Phone,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import type {
  ContainerLineStepPlan,
  ContainerOrderLine,
  ContainerRouteStop,
} from "../../types/containerOrders";
import { IMO_CLASS_OPTIONS } from "../../constants/shipmentFormConstants";

/**
 * One stop, as the whole of what the driver is being asked to do.
 *
 * What this replaces was a stepper: every stop on the route rendered as a card,
 * the current one open and the rest collapsed. That was already an improvement
 * on the flat list of steps before it, and it still had the same shape of
 * problem — the screen was a list of things, one of which was the job.
 *
 * A driver at a terminal gate is doing exactly one thing. So the screen shows
 * exactly one thing: this stop, its facts, its note, its two buttons, and the
 * panel of figures that unlocks between them. The rest of the route is not
 * hidden — it is one card further down, collapsed to a line — but it is
 * reference, and it belongs below the fold where reference goes.
 *
 * The frame is deliberately not collapsible. Everything else on this screen
 * folds; this does not, because there is no state of the run in which the
 * driver wants it shut.
 */

/** `Aug 12, 12:03` — the slot the office booked, in one line. */
const due = (stop: ContainerRouteStop) => {
  const date = stop.date ? new Date(stop.date) : null;
  const day =
    date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(undefined, { day: "numeric", month: "short" })
      : "";
  return [day, stop.time].filter(Boolean).join(", ");
};

/** `11:58` — a stamp, at the precision anybody reads one at. */
const at = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
      });
};

/**
 * Where to send a maps app for this stop.
 *
 * Coordinates when the stop has them, which is every stop picked from the
 * directory — they are captured at that moment precisely so a truck can be sent
 * here. The written address is the fallback, and it is a worse one: a geocoder
 * asked for "TERMINAL, TALLINN" will find *a* terminal.
 */
function mapsHref(stop: ContainerRouteStop): string | null {
  const { latitude, longitude } = stop.address?.coordinates ?? {};
  if (typeof latitude === "number" && typeof longitude === "number") {
    return `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
  }

  const written = [
    stop.companyName,
    stop.address?.street,
    stop.address?.postalCode,
    stop.address?.city,
  ]
    .filter(Boolean)
    .join(", ");

  return written
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
        written,
      )}`
    : null;
}

interface DriverStopFrameProps {
  line: ContainerOrderLine;
  stop: ContainerRouteStop;
  /** 1-based position among the stops actually being made. */
  position: number;
  /** The parent order's number — what the paperwork was filed under. */
  orderNumber?: string;
  /** The step this stop is waiting on, or null once both are stamped. */
  nextStep: ContainerLineStepPlan | null;
  /** True once the driver has reported in here. */
  arrived: boolean;
  /** A step or a save is in flight. */
  busy: boolean;
  /**
   * What this stop is blocked on, as translated field names.
   *
   * Empty means nothing is owed. Non-empty disables the confirm and says why —
   * the frame does not decide *which* gate is being enforced, only draws it.
   */
  blockedBy: string[];
  /** Whether any of that is the driver's own to type in, above. */
  fillableHere: boolean;
  onConfirm: (step: ContainerLineStepPlan) => void;
  /** The details panel — locked before arrival, editable after. */
  children: React.ReactNode;
  /**
   * The container's documents, drawn inside the frame.
   *
   * Passed in rather than rendered here because filing a document is a request
   * against the container, and this component is a drawing of one stop — it
   * holds no `lineId`, does no uploading, and should not start.
   */
  documents?: React.ReactNode;
}

export function DriverStopFrame({
  line,
  stop,
  position,
  orderNumber,
  nextStep,
  arrived,
  busy,
  blockedBy,
  fillableHere,
  onConfirm,
  children,
  documents,
}: DriverStopFrameProps) {
  const { t } = useTranslation("containerOrders");

  const title =
    stop.role === "custom"
      ? stop.label?.trim() ||
        stop.companyName?.trim() ||
        t(`route.stopKinds.${stop.kind ?? "other"}`)
      : t(`route.${stop.role}`);

  const href = mapsHref(stop);
  const blocked = blockedBy.length > 0;
  const cancelled = line.status === "cancelled";

  /**
   * The handling flags, all three stated whether set or not.
   *
   * A flag shown as "No" is an answer; a flag shown as nothing is
   * indistinguishable from a screen that never asked, and "is this box ADR?" is
   * not a question to leave a driver guessing at. Inside the frame rather than
   * floating above it, because it is a fact about the box being handled *here*
   * — and because on a phone a row above the card reads as chrome.
   */
  const flags = [
    {
      key: "adr",
      on: !!line.handling?.adr,
      label: t("handling.adr"),
      detail:
        IMO_CLASS_OPTIONS.find((o) => o.value === line.handling?.imoClass)
          ?.label ?? "",
      tone: "bg-[#FBE9E7] text-[#A63A39]",
    },
    {
      key: "temperatureSensitive",
      on: !!line.handling?.temperatureSensitive,
      label: t("handling.temperature"),
      detail: line.handling?.temperatureRange ?? "",
      tone: "bg-[#E7F0F7] text-[#2C5F82]",
    },
    {
      key: "fragile",
      on: !!line.handling?.fragile,
      label: t("handling.fragile"),
      detail: line.handling?.specifications ?? "",
      tone: "bg-[#FCEEE2] text-[#854F0B]",
    },
  ];

  /**
   * The stop's facts, in the order they get asked for.
   *
   * Order nr leads because it is what every gate, every phone call and every
   * piece of paper is indexed by — and it is the *order's* number rather than
   * the container's position within it. A driver quoting "CO002-01" at a
   * booking desk is quoting something the desk has never seen.
   *
   * The PIN is second and set apart, because it is the reason a driver used to
   * ring the office from a barrier: it has been stored per stop all along, for
   * exactly this, and the driver's own screen was the one place it never
   * appeared.
   *
   * Blank facts are dropped rather than shown as dashes. A row of em-dashes is
   * a screen apologising for itself; the fields that are filled in are the ones
   * worth the height on a phone.
   */
  const facts = [
    {
      key: "orderNr",
      label: t("driver.orderNr"),
      value: orderNumber,
      mono: true,
    },
    { key: "pin", label: t("route.pin"), value: stop.pin, emphasis: true },
    { key: "due", label: t("driver.stopDue"), value: due(stop) },
    {
      key: "ref",
      label: t("driver.stopRef"),
      value: stop.bookingRef,
      mono: true,
    },
    {
      key: "booking",
      label: t("driver.bookingNr"),
      value: line.bookingRef,
      mono: true,
    },
    /**
     * The customs MRN, here as well as in the details panel below.
     *
     * The duplication is deliberate and it is the whole point of putting it
     * here. The panel is where the number is *typed*, and it is locked until
     * the driver has reported in — but an MRN lodged by the office before
     * departure is quoted at the first barrier, which is before the reporting
     * in. A number that only appeared after arrival would be invisible at the
     * one gate that most often asks for it.
     *
     * So: this row is the quotable reference, present at every stop from the
     * moment there is one; the panel below is the editor.
     *
     * And it is the one fact here that is drawn even when it is blank. The rest
     * of the table drops what is unfilled, because an absent ramp is simply not
     * part of this stop — but an absent MRN is a thing a customs officer is
     * about to ask for, and a row that vanished would leave the driver unable
     * to tell "no declaration has been lodged" from "this screen does not carry
     * one". The dash is the answer to a question that does get asked.
     */
    {
      key: "mrn",
      label: t("driver.mrnNumber"),
      value: line.mrnNumber,
      mono: true,
      always: true,
    },
    { key: "ramp", label: t("driver.ramp"), value: stop.ramp },
    {
      key: "containerType",
      label: t("driver.containerType"),
      value: line.containerType
        ? t(`containerTypes.${line.containerType}`, {
            defaultValue: line.containerType,
          })
        : "",
    },
    /**
     * What the box is carrying, straight under the type.
     *
     * The pair is how a gate asks — "40ft high cube, full" — and it is read
     * only. It is a declaration the office makes to the terminal, so a driver
     * who could revise it at the barrier would be revising something somebody
     * else signed.
     *
     * Always drawn, and a container nobody has described says so in as many
     * words rather than falling back to "empty". No answer and the wrong answer
     * are not the same, and only one of them sends a truck to the wrong lane —
     * but a driver who cannot see the row at all has no way to know which of
     * the two they are in, and "not stated" is the thing to ring the office
     * about.
     */
    {
      key: "loadStatus",
      label: t("driver.loadStatus"),
      value: line.loadStatus
        ? t(`loadStatus.${line.loadStatus}`)
        : t("loadStatus.notStated"),
      always: true,
    },
    // Facts that are simply not part of this stop drop out; the two above are
    // questions somebody asks at a barrier, so they answer even when the answer
    // is that nothing has been recorded.
  ].filter((fact) => fact.always || !!String(fact.value ?? "").trim());

  return (
    <section className="overflow-hidden rounded-[14px] border border-[#EF9F27] bg-[#FAEEDA]">
      {/* ── Which stop, and where ─────────────────────────────────────────── */}
      <header className="flex items-start gap-2.5 px-3 pt-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#EF9F27] text-[12px] font-semibold text-white">
          {position}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[15px] font-semibold text-[#1A1A18]">
            {title}
          </h2>
          <p className="flex items-center gap-1 truncate text-[12px] text-[#854F0B]">
            <MapPin className="h-3 w-3 shrink-0" />
            {[stop.companyName, stop.address?.city].filter(Boolean).join(", ")}
          </p>
        </div>
      </header>

      {/* ── The two things the driver does here ────────────────────────────
          Navigate is a link and not a step: it records nothing, and it is the
          first thing wanted at a stop nobody has reached yet. The arrival
          button does not vanish once pressed — it becomes the record of itself,
          so the frame still says what time they reported in an hour later. */}
      <div className="flex gap-2 px-3 pt-3">
        <a
          href={href ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!href}
          className={`flex min-h-[38px] flex-1 items-center justify-center gap-1.5 rounded-[18px] px-3 py-2.5 text-[12.5px] font-semibold ${
            href
              ? "bg-[#8A8A82] text-white"
              : "pointer-events-none bg-[#E4E2DA] text-[#A6A49C]"
          }`}
        >
          <Navigation className="h-4 w-4" />
          {t("driver.navigate")}
        </a>

        {arrived ? (
          <span className="flex min-h-[38px] flex-1 items-center justify-center gap-1.5 rounded-[18px] bg-[#E9F3DC] px-3 py-2.5 text-[12.5px] font-semibold text-[#3B6D11]">
            <Check className="h-4 w-4" />
            {t("driver.arrivedAt", { time: at(stop.arrivedAt) })}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => nextStep && onConfirm(nextStep)}
            disabled={busy || blocked || cancelled || !nextStep}
            title={blocked ? blockedBy.join(", ") : undefined}
            className="flex min-h-[38px] flex-1 items-center justify-center gap-1.5 rounded-[18px] bg-[#BA7517] px-3 py-2.5 text-[12.5px] font-semibold text-white disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MapPin className="h-4 w-4" />
            )}
            {t("driver.imHere")}
          </button>
        )}
      </div>

      {/* ── How this box has to be handled ────────────────────────────────── */}
      <div className="flex flex-wrap gap-1.5 px-3 pt-3">
        {flags.map(({ key, on, label, detail, tone }) => (
          <span
            key={key}
            className={`inline-flex items-center gap-1 rounded-[20px] px-2.5 py-1 text-[11px] ${
              on ? `font-semibold ${tone}` : "bg-white text-[#6B6A64]"
            }`}
          >
            {on && <AlertTriangle className="h-3 w-3 shrink-0" />}
            {label}
            <span className={on ? "font-normal opacity-90" : "opacity-70"}>
              · {on ? detail || t("readonly.yes") : t("readonly.no")}
            </span>
          </span>
        ))}
      </div>

      {/* ── The stop's facts ─────────────────────────────────────────────── */}
      {facts.length > 0 && (
        <dl className="mx-3 mt-3 rounded-[10px] bg-white px-3 py-1">
          {facts.map(({ key, label, value, mono, emphasis }) => (
            <div
              key={key}
              className="flex items-baseline justify-between gap-3 py-[3px]"
            >
              <dt className="shrink-0 text-[13px] text-[#6B6A64]">{label}</dt>
              <dd
                className={`min-w-0 break-words text-right text-[13px] text-[#1A1A18] ${
                  mono ? "font-mono text-[12.5px]" : ""
                } ${
                  emphasis
                    ? "text-[15px] font-bold tracking-wide"
                    : "font-medium"
                }`}
              >
                {String(value ?? "").trim() || "—"}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Somebody to ring, when the stop names one. Its own row rather than a
          fact in the table, because it is an action. */}
      {stop.contactPhone && (
        <a
          href={`tel:${stop.contactPhone}`}
          className="mx-3 mt-2 flex items-center gap-2 rounded-[10px] bg-white px-3 py-2.5 text-[13px] font-medium text-[#854F0B]"
        >
          <Phone className="h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 truncate">
            {[stop.contactName, stop.contactPhone].filter(Boolean).join(" · ")}
          </span>
        </a>
      )}

      {/* ── What dispatch said about this visit ────────────────────────────
          Prose, so it gets a block of its own rather than a row in the table
          above — it is read, not looked up. The byline is the point: an
          instruction written this morning by the dispatcher who booked the slot
          is current, and one written three weeks ago by somebody who has since
          left is a trap. Without a name and a time the two are the same grey
          paragraph. Stops written before authorship was recorded show the note
          with no byline, which is honest — inventing one would be worse. */}
      {stop.comment?.trim() && (
        <div className="mx-3 mt-2 rounded-[10px] bg-white px-3 py-2.5">
          <p className="mb-1 flex items-center gap-1.5 text-[11px] font-medium text-[#A6A49C]">
            <Info className="h-3.5 w-3.5 shrink-0" />
            {t("driver.dispatcherNote")}
          </p>
          <p className="whitespace-pre-wrap text-[13px] font-medium leading-snug text-[#1A1A18]">
            {stop.comment}
          </p>
          {stop.commentAt && (
            <p className="mt-1 text-[11px] text-[#A6A49C]">
              {t("route.stopCommentBy", {
                who:
                  `${stop.commentBy?.firstName ?? ""} ${
                    stop.commentBy?.lastName ?? ""
                  }`.trim() || t("route.stopCommentUnknownAuthor"),
                when: at(stop.commentAt),
              })}
            </p>
          )}
        </div>
      )}

      {/* ── The box's own figures ──────────────────────────────────────────
          Locked until the driver reports in, and that is the change this whole
          screen was rebuilt around. These used to be demanded *before* the
          first step — a container number, a plated tare, a payload rating,
          asked for while the driver was still in the cab on the way to collect
          a box nobody had seen. The only way to satisfy it was to guess.

          Now the arrival is free and the departure is gated: report in, read
          the door, fill this in, confirm. The server draws the same line at the
          same step; see LINE_DEPARTURE_REQUIREMENTS. */}
      {/* `mb-3` and not only `mt-3`. Before the driver reports in there is no
          confirm button beneath this, so the panel was the last thing in the
          frame with nothing under it — and its dashed border sat directly on
          the frame's own, reading as one broken double line rather than as a
          card inside a card. The margin is what separates them. */}
      <div className="mx-3 mb-3 mt-3 space-y-2">
        {children}

        {/* ── The paperwork for this stop ────────────────────────────────
            Inside the frame, because that is where it is used: the gate
            receipt is handed over in the same minute as the confirmation, and
            a driver should not have to leave the card they are working — or
            scroll past the route — to photograph it. It sits after the box's
            figures and before the confirm, which is the order the stop
            actually happens in: read the door, take the paper, drive on.

            Only while there is something to do here. On a cancelled container
            the page keeps the standalone block instead, where the list is
            still readable and nothing can be added to it. */}
        {documents}
      </div>

      {/* ── Leaving ────────────────────────────────────────────────────────
          Only once the arrival is stamped, because until then it is not the
          next thing and offering it would be offering a step the server
          refuses. Greyed rather than hidden while something is outstanding, and
          the reason printed underneath — a disabled button with no explanation
          is a dead end at a terminal gate. */}
      {arrived && nextStep && !cancelled && (
        <div className="p-3">
          {blocked && (
            <p className="mb-2 rounded-lg bg-[#FBEDED] px-3 py-2 text-[12px] text-[#A63A39]">
              {t("driver.blocked.beforeLeaving", {
                fields: blockedBy.join(", "),
              })}
              {fillableHere ? ` ${t("driver.blocked.fillAbove")}` : ""}
            </p>
          )}

          <button
            type="button"
            onClick={() => onConfirm(nextStep)}
            disabled={busy || blocked}
            className="flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#BA7517] px-4 py-[13px] text-[14.5px] font-semibold text-white disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("driver.confirmAndContinue")}
          </button>
        </div>
      )}
    </section>
  );
}

export default DriverStopFrame;
